import { useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, Loader2, MessageCircle, CheckCircle2 } from "lucide-react";

const HEADLINES = {
  de: {
    eyebrow: "Anfrage zur Leistung",
    title: (s) => `${s} – jetzt unverbindlich anfragen`,
    subtitle:
      "Kostenlose Erst-Beratung auf Deutsch. Wir antworten meist innerhalb von 24 Stunden.",
    pitch: "Antwort meist innerhalb 24 Stunden · keine versteckten Kosten · Beratung kostenlos",
    or: "oder",
    whatsapp: "Direkt auf WhatsApp",
    successTitle: "Vielen Dank!",
    successBody: "Wir melden uns in Kürze bei Ihnen.",
    validation: "Bitte alle Pflichtfelder ausfüllen.",
  },
  en: {
    eyebrow: "Service enquiry",
    title: (s) => `${s} – request a free quote`,
    subtitle: "Free initial consultation. We usually reply within 24 hours.",
    pitch: "Reply within 24 h · no hidden costs · free consultation",
    or: "or",
    whatsapp: "Message on WhatsApp",
    successTitle: "Thank you!",
    successBody: "We'll get back to you shortly.",
    validation: "Please fill all required fields.",
  },
  es: {
    eyebrow: "Solicitud de servicio",
    title: (s) => `${s} – pida un presupuesto sin compromiso`,
    subtitle: "Consulta inicial gratuita. Respondemos en menos de 24 horas.",
    pitch: "Respuesta en 24 h · sin costes ocultos · consulta gratis",
    or: "o",
    whatsapp: "Escribir por WhatsApp",
    successTitle: "¡Gracias!",
    successBody: "Le contactaremos en breve.",
    validation: "Por favor rellene los campos obligatorios.",
  },
};

const ServiceContactForm = ({ slug, serviceTitle, waUrl }) => {
  const { t, locale } = useLanguage();
  const copy = HEADLINES[locale] || HEADLINES.de;

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(copy.validation);
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        locale,
        service: `service-detail-${slug}`,
        phone: form.phone || null,
        source_domain: typeof window !== "undefined" ? window.location.hostname : null,
      });
      setSubmitted(true);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="service-anfrage"
      data-testid="service-contact-section"
      className="bg-gradient-to-b from-[#f7f2ea] to-[#fdfbf7] scroll-mt-24"
    >
      <div className="container-px mx-auto max-w-7xl py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">
              {copy.eyebrow}
            </div>
            <h2 className="font-display mt-3 text-3xl lg:text-5xl font-light tracking-tighter text-neutral-950 text-balance">
              {copy.title(serviceTitle)}
            </h2>
            <p className="mt-5 text-neutral-700 leading-relaxed max-w-md">
              {copy.subtitle}
            </p>
            <p className="mt-6 text-sm text-neutral-500">{copy.pitch}</p>

            {waUrl && (
              <div className="mt-10">
                <div className="text-xs uppercase tracking-[0.22em] text-neutral-400 mb-3">
                  {copy.or}
                </div>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="service-contact-whatsapp"
                  className="inline-flex items-center gap-3 rounded-full bg-emerald-500 text-white px-6 py-3.5 text-sm font-medium hover:bg-emerald-600 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  {copy.whatsapp}
                </a>
              </div>
            )}
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="service-contact-success"
                className="rounded-3xl bg-white border border-emerald-200 p-10 lg:p-12 text-center"
              >
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 grid place-items-center">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-display mt-5 text-2xl text-neutral-950">
                  {copy.successTitle}
                </h3>
                <p className="mt-3 text-neutral-600">{copy.successBody}</p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm text-sky-700 hover:underline"
                  data-testid="service-contact-reset"
                >
                  {locale === "de" ? "Weitere Anfrage senden" : locale === "es" ? "Otra solicitud" : "Send another request"}
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="service-contact-form"
                className="rounded-3xl bg-white border border-neutral-200 p-7 lg:p-10 shadow-[0_18px_40px_rgba(0,0,0,0.04)]"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor={`sc-name-${slug}`}>{t("contact.name")} *</Label>
                    <Input
                      id={`sc-name-${slug}`}
                      data-testid="service-contact-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      required
                      className="rounded-xl bg-white border-neutral-200 h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`sc-email-${slug}`}>{t("contact.email")} *</Label>
                    <Input
                      id={`sc-email-${slug}`}
                      type="email"
                      data-testid="service-contact-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      required
                      className="rounded-xl bg-white border-neutral-200 h-12"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`sc-phone-${slug}`}>{t("contact.phone")}</Label>
                    <Input
                      id={`sc-phone-${slug}`}
                      data-testid="service-contact-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className="rounded-xl bg-white border-neutral-200 h-12"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`sc-message-${slug}`}>{t("contact.message")} *</Label>
                    <Textarea
                      id={`sc-message-${slug}`}
                      data-testid="service-contact-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      required
                      rows={4}
                      className="rounded-xl bg-white border-neutral-200"
                    />
                  </div>
                </div>

                <div className="mt-7 flex items-center justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="service-contact-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-7 py-3.5 text-sm font-medium hover:bg-black disabled:opacity-60 transition-colors"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {t("contact.submit")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceContactForm;
